import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiClientError } from '../../services/apiClient'
import {
  adminClient,
  type AdminPaymentDetail,
  type AdminPaymentListItem,
  type AdminPaymentStatus,
} from '../../services/adminClient'
import { formatMxn } from '../../services/suscripcionClient'
import { Modal } from '../../components/Modal'

const PAGE_SIZE = 25
const SEARCH_DEBOUNCE_MS = 300

const STATUS_LABELS: Record<AdminPaymentStatus, string> = {
  pending: 'Pendiente',
  paid: 'Pagado',
  failed: 'Fallido',
}

const STATUS_BADGE_CLASS: Record<AdminPaymentStatus, string> = {
  pending: 'badge--draft',
  paid: 'badge--closed',
  failed: 'badge--failed',
}

/**
 * Historial de pagos desde el portal admin (sección Finanzas).
 *   - Lazy load 25 por carga.
 *   - Búsqueda por tenant / referencia / confirmación bancaria.
 *   - Click en fila → modal con detalle completo del pago.
 */
export function FinanzasPage() {
  const [items, setItems] = useState<AdminPaymentListItem[]>([])
  const [cursor, setCursor] = useState<number | null>(null)
  const [hasMore, setHasMore] = useState(true)
  const [total, setTotal] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [status, setStatus] = useState<AdminPaymentStatus | ''>('')

  const [selected, setSelected] = useState<AdminPaymentDetail | null>(null)

  const sentinelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [search])

  const loadFirst = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await adminClient.finanzas.list({
        q: debounced || undefined,
        status: status || undefined,
        limit: PAGE_SIZE,
      })
      setItems(res.items)
      setCursor(res.next_cursor)
      setHasMore(res.next_cursor !== null)
      setTotal(res.total)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo cargar.')
    } finally {
      setLoading(false)
    }
  }, [debounced, status])

  useEffect(() => { loadFirst() }, [loadFirst])

  useEffect(() => {
    if (!hasMore || cursor === null) return
    const node = sentinelRef.current
    if (!node) return
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !loading) loadMore(cursor)
    }, { rootMargin: '200px' })
    obs.observe(node)
    return () => obs.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, hasMore, loading])

  async function loadMore(from: number) {
    setLoading(true)
    try {
      const res = await adminClient.finanzas.list({
        q: debounced || undefined,
        status: status || undefined,
        cursor: from,
        limit: PAGE_SIZE,
      })
      setItems((prev) => [...prev, ...res.items])
      setCursor(res.next_cursor)
      setHasMore(res.next_cursor !== null)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo cargar más.')
    } finally {
      setLoading(false)
    }
  }

  async function openDetail(payment: AdminPaymentListItem) {
    try {
      const res = await adminClient.finanzas.show(payment.id)
      setSelected(res.payment)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo cargar el pago.')
    }
  }

  return (
    <div className="content-stack">
      <header className="list-toolbar">
        <div className="list-toolbar__title">
          <h1>Finanzas</h1>
          {total !== null && <span className="muted">{total.toLocaleString('es-MX')} transacciones</span>}
        </div>
        <div className="list-toolbar__actions">
          <select value={status} onChange={(e) => setStatus(e.target.value as AdminPaymentStatus | '')}>
            <option value="">Todos los estados</option>
            <option value="paid">Pagado</option>
            <option value="pending">Pendiente</option>
            <option value="failed">Fallido</option>
          </select>
          <input
            type="search"
            className="search-input"
            placeholder="Buscar por tenant o referencia…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </header>

      {error && <p className="error">{error}</p>}

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Tenant</th>
              <th>Usuario</th>
              <th>Monto</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr><td colSpan={5} className="data-table__empty">No hay transacciones.</td></tr>
            )}
            {items.map((p) => (
              <tr key={p.id} className="data-table__row" tabIndex={0}
                  onClick={() => openDetail(p)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDetail(p) } }}>
                <td className="mono">{shortDate(p.paid_at ?? p.created_at)}</td>
                <td><strong>{p.tenant_name}</strong></td>
                <td>{userLabel(p)}</td>
                <td className="mono">{formatMxn(p.amount)}</td>
                <td>
                  <span className={`badge ${STATUS_BADGE_CLASS[p.status]}`}>
                    {STATUS_LABELS[p.status]}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && <p className="muted center">Cargando…</p>}
        {hasMore && cursor !== null && <div ref={sentinelRef} aria-hidden="true" style={{ height: 1 }} />}
      </div>

      <Modal
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selected ? selected.tenant_name : 'Pago'}
        description={selected?.reference}
        size="md"
        footer={
          <div className="modal__actions">
            <button type="button" className="ghost-btn" onClick={() => setSelected(null)}>Cerrar</button>
          </div>
        }
      >
        {selected && (
          <dl className="detail-dl">
            <dt>Estado</dt>
            <dd>
              <span className={`badge ${STATUS_BADGE_CLASS[selected.status]}`}>
                {STATUS_LABELS[selected.status]}
              </span>
            </dd>

            <dt>Monto</dt>
            <dd className="mono">{formatMxn(selected.amount)}</dd>

            <dt>Número de transacción</dt>
            <dd className="mono">{selected.reference}</dd>

            <dt>Confirmación bancaria</dt>
            <dd className="mono">{selected.bank_confirmation ?? '—'}</dd>

            <dt>Plan</dt>
            <dd>{selected.plan_name}</dd>

            <dt>Vigencia cubierta</dt>
            <dd className="mono">
              {selected.covers_start_date && selected.covers_end_date
                ? `${formatDateDMY(selected.covers_start_date)} – ${formatDateDMY(selected.covers_end_date)}`
                : <span className="muted">N/A (pago anterior al registro de vigencia)</span>}
            </dd>

            <dt>Tenant</dt>
            <dd>{selected.tenant_name}</dd>

            <dt>Usuario</dt>
            <dd>{userLabel(selected)}</dd>

            <dt>Fecha de transacción</dt>
            <dd className="mono">{formatDateDMY(selected.created_at)}</dd>

            {selected.paid_at && (
              <>
                <dt>Fecha de pago confirmado</dt>
                <dd className="mono">{formatDateDMY(selected.paid_at)}</dd>
              </>
            )}
          </dl>
        )}
      </Modal>
    </div>
  )
}

function userLabel(p: AdminPaymentListItem): string {
  if (!p.user_email) return '—'
  const name = [p.user_first_name, p.user_last_name_paternal].filter(Boolean).join(' ')
  return name !== '' ? `${name} (${p.user_email})` : p.user_email
}

function shortDate(value: string | null): string {
  if (!value) return '—'
  try {
    return new Date(value.replace(' ', 'T')).toLocaleDateString('es-MX', {
      year: 'numeric', month: 'short', day: '2-digit',
    })
  } catch {
    return value
  }
}

const MONTHS_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

/** Formato "10-Jul-2026". */
function formatDateDMY(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value.replace(' ', 'T'))
  if (isNaN(d.getTime())) return value
  const day = String(d.getDate()).padStart(2, '0')
  return `${day}-${MONTHS_ES[d.getMonth()]}-${d.getFullYear()}`
}
